import React from 'react';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { SEGMENT_COLORS, UI_LABELS } from '../../../constants/segment-colors';
import { getTextColorClass } from '../../../utils/color-contrast';

interface SegmentProps {
  segment?: string | null;
  handleSelectSegment?: (segment: string) => void;
}

const Segment: React.FC<SegmentProps> = ({ segment, handleSelectSegment }) => {
  if (!segment) {
    return <span className="text-muted-foreground">-</span>;
  }

  const segmentKey = segment;
  const label = UI_LABELS[segmentKey] || segment;

  const getSegmentColor = (segmentKey: string) => {
    const hexColor = SEGMENT_COLORS[segmentKey as keyof typeof SEGMENT_COLORS];

    if (hexColor) {
      // Use the color contrast utility to determine appropriate text color
      const textColor = getTextColorClass(hexColor);
      return {
        className: `${textColor}`,
        style: { backgroundColor: hexColor, borderColor: hexColor },
      };
    }

    return {
      className: 'bg-gray-100 text-gray-800 border-gray-200',
      style: {},
    };
  };

  const handleClick = () => {
    if (handleSelectSegment) {
      handleSelectSegment(segment);
    }
  };

  const isClickable = !!handleSelectSegment;

  const { className: colorClass, style: colorStyle } = getSegmentColor(segmentKey);

  return (
    <Badge
      variant="outline"
      className={`
        ${colorClass}
        text-xs font-medium px-2 py-1
        ${isClickable ? 'cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500' : ''}
      `}
      style={colorStyle}
      onClick={isClickable ? handleClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      } : undefined}
      aria-label={isClickable ? `Filter by ${label} segment` : `Segment: ${label}`}
      title={isClickable ? `Click to filter by ${label} segment` : undefined}
    >
      {label}
    </Badge>
  );
};

export default Segment;
